// Same ranks as the app (soskills-app/src/helpers/rank.ts) and the cron that gives the points.
type Rank = 'recruit' | 'soldier' | 'veteran' | 'elite' | 'legend';

interface RankTier {
  rank: Rank;
  points: number;
}

// Points needed to reach each rank.
const RANK_TIERS: RankTier[] = [
  { rank: 'recruit', points: 0 },
  { rank: 'soldier', points: 150 },
  { rank: 'veteran', points: 500 },
  { rank: 'elite', points: 1500 },
  { rank: 'legend', points: 3500 },
];

// Points earned each week by the best video of each game, from the top 1 to the top 10.
const RANK_POINTS = [100, 80, 65, 50, 40, 30, 25, 20, 15, 10];

const RANK_COLORS: Record<Rank, string> = {
  recruit: '#8A8A8A',
  soldier: '#CD7F32',
  veteran: '#C0C0C0',
  elite: '#FFD700',
  legend: '#7DD3FC',
};

// Notification sent when a user reaches a new rank.
const RANK_UP_NOTIFICATION = 'rankUp';

const isRank = (value?: string | null): value is Rank => (
  RANK_TIERS.some((tier) => tier.rank === value)
);

const getRankTier = (rank?: string | null) => (
  RANK_TIERS.find((tier) => tier.rank === rank) || RANK_TIERS[0]
);

// The tier after the given rank, null for the last one.
const getNextRank = (rank?: string | null): RankTier | null => {
  const index = RANK_TIERS.findIndex((tier) => tier.rank === rank);
  return RANK_TIERS[Math.max(index, 0) + 1] || null;
};

// Progress from 0 to 1 between the current rank and the next one.
const getRankProgress = (rank?: string | null, points = 0) => {
  const current = getRankTier(rank);
  const next = getNextRank(rank);
  if (!next) {
    return 1;
  }
  const progress = (points - current.points) / (next.points - current.points);
  return Math.min(Math.max(progress, 0), 1);
};

export type { Rank, RankTier };
export {
  RANK_COLORS,
  RANK_POINTS,
  RANK_TIERS,
  RANK_UP_NOTIFICATION,
  getNextRank,
  getRankProgress,
  getRankTier,
  isRank,
};
